import { ArrowLeft, ExternalLink, Maximize, Share } from 'react-feather';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddScannerMutation as AddScanner } from '__generated__/graphql';
import { CopyLinkInput } from '../../../common/Common/CopyLink';
import { assetUrl, encodeUrl, isGraphQLValidationError } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddScannerForm from './AddScannerForm';
import AddScannerMutation from './AddScannerMutation';
import EventPicker, { Event } from '../../Events/EventPicker';
import GetEventsCountQuery from '../GetEventsCountQuery';
import GetScannersForEventQuery from './GetScannersForEventQuery';
import NoEventsCard from '../NoEventsCard';
import ScannerCard from './ScannerCard';
import UI from '../../../common/UI';
import config from '../../config';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useScroll from '../../../common/useScroll';
import useShare from '../../../common/useShare';

export interface ScannersPageProps {
  scannerId?: string;
}

const ScannersPage = ({ scannerId }: ScannersPageProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { scrollToIfInvisible } = useScroll();
  const history = useHistory();
  const notifier = useNotifier();
  const project = useProject();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('scanners'));

  const { data: eventsData } = useQuery(
    GetEventsCountQuery,
    {
      variables: {
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data, loading, refetch } = useQuery(
    GetScannersForEventQuery,
    {
      variables: {
        project_id: project.id,
        event_id: recentEvent.id,
      },
      skip: !recentEvent.id,
      onError: (error) => {
        if (isGraphQLValidationError(error)) {
          // Recent event no longer exists, reset it.
          recentEvent.reset();
        }
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const event = data?.project.event.id === recentEvent.id ? data?.project.event : null;
  const scanners = event?.scanners;

  const [openAddScannerModal, closeAddScannerModal] = useModal(() => {
    const handleSuccess = (scanner: AddScanner['addScanner']) => {
      closeAddScannerModal();
      history.push(`/${project.id}/participants/scanners/${scanner.id}?${encodeUrl({ event: recentEvent.id })}`);
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddScannerModal}>
        <AddScannerForm
          event={event}
          onSuccess={handleSuccess}
        />
      </UI.Modal>
    );
  }, [project, event]);

  const scannerUrl = event ? `${event.scanner_url}?locale=${locale}` : null;
  const { canShare, handleShare } = useShare({
    title: t('atleta_scanner'),
    text: t('atleta_scanner_share_text', { url: scannerUrl }),
  });

  const [openScanModal, closeScanModal] = useModal(() => (
    <UI.Modal maxWidth={600} close={closeScanModal}>
      <UI.GridContainer>
        <UI.H3>{t('scan')}</UI.H3>

        <UI.HR />

        <UI.Div>
          <UI.H4 sc={{ mb: 1 }}>{t('scan_from_this_device')}</UI.H4>
          <UI.Paragraph>
            {t('scan_from_this_device_description')}
          </UI.Paragraph>
          <UI.AButton
            href={`${scannerUrl}&token=${event.scanner_token}`}
            target="_blank"
            rel="noopener noreferrer"
            sc={{ brand: 'secondary' }}
          >
            {t('open_scanner')}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.AButton>
        </UI.Div>

        <UI.HR />

        <UI.GridContainer>
          <UI.GridContainer sc={{ columns: '1fr 140px' }}>
            <UI.Div>
              <UI.H4 sc={{ mb: 1 }}>{t('scan_from_another_device')}</UI.H4>
              <UI.Paragraph>
                {t('scan_from_another_device_description')}
              </UI.Paragraph>

              {t('password')}
              {': '}
              <UI.Strong>{event.scanner_password}</UI.Strong>
            </UI.Div>
            <UI.Div>
              <img
                src={`${config.eventScannerUrl}/image/qr.png?${encodeUrl({
                  value: scannerUrl,
                  size: 300,
                })}`}
                width={140}
                height={140}
                style={{ display: 'block' }}
                alt={t('qr_code')}
              />
            </UI.Div>
          </UI.GridContainer>

          <UI.FormGrid sc={{ columns: ['1fr', '1fr fit-content(250px)'], gutter: [1, 0], mb: 2 }}>
            <CopyLinkInput value={scannerUrl} />
            <UI.Div>
              {canShare && (
                <UI.Button onClick={handleShare} sc={{ outline: true, ml: 2 }}>
                  <UI.Icon>
                    <Share />
                  </UI.Icon>
                  {' '}
                  {t('share')}
                </UI.Button>
              )}
            </UI.Div>
          </UI.FormGrid>
        </UI.GridContainer>
      </UI.GridContainer>
    </UI.Modal>
  ), [canShare, event, scannerUrl]);

  useEffect(() => {
    if (event && scanners.filter(({ id }) => id === scannerId).length === 0) {
      refetch().then(() => {
        scrollToIfInvisible(`ScannerCard_${scannerId}`, { gutter: 0.5 });
      });
    }
  }, [scannerId]);

  const handleEventChange = (event: Event) => {
    history.push(`/${project.id}/participants/scanners?${encodeUrl({ event: event.id })}`);
  };

  const [addScanner, { loading: adding }] = useMutation(
    AddScannerMutation,
    {
      variables: {
        input: {
          event: { id: event?.id },
          info: {
            title: t('default_scanner_title'),
          },
        },
      },
      onCompleted: () => {
        refetch();
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  if (!eventsData || (recentEvent.id && !event)) {
    return <UI.PageLoader />;
  }

  if (eventsData.project.events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer
          sc={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <UI.SectionHeading sc={{ inlineBlock: true }}>
            {t('scanners')}
          </UI.SectionHeading>
        </UI.FlexContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }} style={{ zIndex: 10 }}>
          <UI.Div>
            <EventPicker
              eventId={recentEvent.id}
              onChange={handleEventChange}
              nullable={false}
              renderActive={(event) => (
                <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                  {event?.title || t('choose_event')}
                </UI.Span>
              )}
            />
          </UI.Div>
          {!recentEvent.id && (
            <UI.Div>
              <UI.Card
                sc={{ py: 0 }}
                style={{ lineHeight: '40px', borderRadius: '8px !important' }}
              >
                <UI.Icon sc={{ muted: true }}>
                  <ArrowLeft />
                </UI.Icon>
                {' '}
                {t('select_event_first')}
              </UI.Card>
            </UI.Div>
          )}
          {event && scanners.length > 0 && (
            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.Button
                onClick={openAddScannerModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_scanner')}
              </UI.Button>
              {event.scanner_url && (
                <UI.Button
                  onClick={openScanModal}
                  sc={{ brand: 'secondary', ml: 2 }}
                >
                  <UI.Icon>
                    <Maximize />
                  </UI.Icon>
                  {' '}
                  {t('scan')}
                </UI.Button>
              )}
            </UI.Div>
          )}
        </UI.GridContainer>

        {event && (
          <UI.GridContainer sc={{ muted: loading, gutter: 0.5 }} style={{ zIndex: 1 }}>
            {scanners.map((scanner) => (
              <ScannerCard
                scanner={scanner}
                event={event}
                active={scannerId === scanner.id}
                muted={scannerId && scannerId !== scanner.id}
                key={scanner.id}
              />
            ))}
            {scanners.length === 0 && (
              <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                <UI.Div style={{ maxWidth: 600, margin: '0 auto' }}>
                  <img src={assetUrl('images/scanner.svg')} alt="" height={120} />
                  <UI.H3 sc={{ mb: 2 }}>{t('start_scanning_title')}</UI.H3>
                  <UI.Paragraph>
                    {t('no_scanners_get_started')}
                  </UI.Paragraph>
                  <UI.Button
                    onClick={() => addScanner()}
                    sc={{ brand: 'secondary' }}
                    disabled={adding}
                  >
                    {t('get_started')}
                  </UI.Button>
                </UI.Div>
              </UI.Card>
            )}
          </UI.GridContainer>
        )}

        {!recentEvent.id && (
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
            <UI.Div style={{ maxWidth: 600, margin: '0 auto' }}>
              <img src={assetUrl('images/scanner.svg')} alt="" height={120} />
              <UI.H3 sc={{ mb: 2 }}>{t('start_scanning_title')}</UI.H3>
              <UI.Paragraph sc={{ mb: 0 }}>
                {t('no_scanners_get_started')}
              </UI.Paragraph>
            </UI.Div>
          </UI.Card>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

export default ScannersPage;
